import React, { useMemo } from 'react';
import { CustomTileHostLayer } from 'features/layout/tiles/custom-tile-host/CustomTileHostLayer';
import {
  CustomTileHostContext,
  createCustomTileHostRegistry,
} from 'features/layout/tiles/custom-tile-host/registry';
import { selectCustomTilesData } from 'features/layout/features/config/configSlice';
import { useAppSelector } from 'store/hooks';

/**
 * Keeps one host element per custom tile alive for as long as the tile exists in state, so
 * expanding or pinning it moves the host instead of re-injecting the integrator's markup
 * somewhere else in the tree (SAMBA-9469).
 *
 * Every root that renders tiles mounts its own provider, and never more than one at a time —
 * two live hosts would share the `custom-tile-<name>` id the SDK resolves messages through.
 */
export const CustomTileHostProvider = ({ children }: { children: React.ReactNode }) => {
  const customTilesData = useAppSelector(selectCustomTilesData);
  const tiles = useMemo(() => Object.values(customTilesData), [customTilesData]);

  const registry = useMemo(() => createCustomTileHostRegistry(), []);

  return (
    <CustomTileHostContext.Provider value={registry}>
      {children}
      {tiles.length ? <CustomTileHostLayer tiles={tiles} registry={registry} /> : null}
    </CustomTileHostContext.Provider>
  );
};
